/** Kütüphane ekranı: sayfalanmış öğe listesi + alt öğelere (sezon/bölüm) giriş. */

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  api,
  type Item,
  type Library,
  type LibraryFilter,
  type LibrarySort,
  type LibrarySummary,
  type MusicAlbum,
} from '../api.ts';
import {
  FEW_ITEMS_LIMIT,
  showcaseColumns,
  showsPager,
  usesShowcaseGrid,
} from '../catalogLayout.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import { MusicLibraryGroups } from '../components/MusicLibraryGroups.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import { ViewToolbar } from '../components/ViewToolbar.tsx';
import { formatDate, formatTotalDuration } from '../format.ts';
import {
  AlertIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  FilmIcon,
  FolderIcon,
  type IconProps,
  InfoIcon,
  LockIcon,
  MoreIcon,
  MusicIcon,
  QueueIcon,
  RefreshIcon,
  SparklesIcon,
  TrashIcon,
  TvIcon,
} from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaUrl } from '../mediaToken.ts';
import {
  loadOfflineLibraryPage,
  type OfflineLibraryPage,
  saveOfflineLibraryPage,
} from '../offlineCatalog.ts';
import { useSession } from '../session.tsx';
import { useModalFocus } from '../useModalFocus.ts';
import { useViewPrefs, type ViewPrefsApi } from '../viewPrefs.ts';

const PAGE_SIZE = 50;

function isMusicLibrary(library: Library | null): boolean {
  const lower = (library?.name || '').toLowerCase();
  return (
    library?.kind === 'music' ||
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music')
  );
}

interface LibraryTheme {
  /** Kahraman alanındaki büyük ikon. */
  icon: ReactNode;
  /** Bölüm başlığındaki küçük ikon (her yerde görünür olduğu için küçük). */
  sectionIcon: ReactNode;
  /** Boş kütüphane ekranındaki ikon (80 px'lik çerçeveye göre büyük). */
  emptyIcon: ReactNode;
  themeClass: string;
  typeLabel: string;
}

/** Kahraman alanındaki büyük ikon (mavi kutu içinde). */
const HERO_ICON: IconProps = { size: 26 };
/** Bölüm başlığındaki küçük ikon. */
const SECTION_ICON: IconProps = { size: 17 };
/** Boş kütüphane ekranındaki ikon. */
const EMPTY_ICON: IconProps = { size: 38 };

function buildTheme(kind: string, icon: (props: IconProps) => ReactNode): LibraryTheme {
  return {
    icon: icon(HERO_ICON),
    sectionIcon: icon(SECTION_ICON),
    emptyIcon: icon(EMPTY_ICON),
    themeClass: `library-hero--${kind}`,
    typeLabel: '',
  };
}

function getLibraryTheme(kind: string | undefined, name: string | undefined): LibraryTheme {
  const lower = (name || '').toLowerCase();
  const k = kind || '';

  const themeFor = (
    themeKind: string,
    typeLabel: string,
    icon: (props: IconProps) => ReactNode,
  ): LibraryTheme => {
    const base = buildTheme(themeKind, icon);
    return { ...base, typeLabel };
  };

  if (
    k === 'music' ||
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music')
  ) {
    return themeFor('music', 'Müzik Kütüphanesi', (props) => <MusicIcon {...props} />);
  }
  if (k === 'show' || lower.includes('dizi') || lower.includes('series')) {
    return themeFor('show', 'Dizi Kütüphanesi', (props) => <TvIcon {...props} />);
  }
  if (k === 'movie' || lower.includes('film')) {
    return themeFor('movie', 'Film Kütüphanesi', (props) => <FilmIcon {...props} />);
  }
  return themeFor('default', 'Medya Kütüphanesi', (props) => <FolderIcon {...props} />);
}

interface LibraryViewState {
  sort: LibrarySort;
  filter: LibraryFilter;
  offset: number;
}

function libraryViewKey(libraryId: string): string {
  return `pbfork.libraryView.${libraryId}`;
}

/** Oturumda bu kütüphane için açık bir görünüm seçilmiş mi? */
function hasStoredLibraryView(libraryId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(libraryViewKey(libraryId)) !== null;
  } catch {
    return false;
  }
}

/** Kütüphane görünümünü oturumda korur; detaydan dönüşte filtre kaybolmaz. */
function readLibraryView(libraryId: string): LibraryViewState {
  const fallback: LibraryViewState = { sort: 'title', filter: 'all', offset: 0 };
  if (typeof sessionStorage === 'undefined') return fallback;
  try {
    const raw = sessionStorage.getItem(libraryViewKey(libraryId));
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as Partial<LibraryViewState>;
    return {
      sort:
        parsed.sort === 'added' || parsed.sort === 'year' || parsed.sort === 'duration'
          ? parsed.sort
          : 'title',
      filter: parsed.filter === 'unwatched' || parsed.filter === 'watched' ? parsed.filter : 'all',
      offset:
        typeof parsed.offset === 'number' && Number.isInteger(parsed.offset) && parsed.offset >= 0
          ? parsed.offset
          : 0,
    };
  } catch {
    return fallback;
  }
}

function saveLibraryView(libraryId: string, view: LibraryViewState): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(libraryViewKey(libraryId), JSON.stringify(view));
  } catch {
    // Gizli mod / kota: yalnız bu oturumda kalır.
  }
}

export function LibraryScreen({
  libraryId,
  library,
  onOpenItem,
  onWatch,
  onEnqueue,
  onOpenAlbum,
  onRefreshLibraries,
  onDeleted,
  canManage = false,
}: {
  libraryId: string;
  /** Periyodik tazelenen katalogdan bu kütüphanenin güncel durumu (ADR-011). */
  library: Library | null;
  onOpenItem: (itemId: string) => void;
  onWatch: (itemId: string) => void;
  /** Müzik kartındaki "Sıraya ekle" eylemi. */
  onEnqueue?: ((item: Item) => void) | undefined;
  /** Müzik albüm başlığından albüm ekranına (künye/kapak) git. */
  onOpenAlbum?: ((albumId: string) => void) | undefined;
  onRefreshLibraries?: (() => void) | undefined;
  onDeleted?: (() => void) | undefined;
  /** Tarama ve silme yalnız yöneticiye gösterilir. */
  canManage?: boolean;
}): ReactNode {
  const { user } = useSession();
  const initialView = readLibraryView(libraryId);
  const [items, setItems] = useState<Item[] | null>(null);
  const [summary, setSummary] = useState<LibrarySummary | null>(null);
  /** Müzik kütüphanesinde albüm künyeleri (başlık, kapak, düzenleme bağlantısı). */
  const [musicAlbums, setMusicAlbums] = useState<MusicAlbum[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(initialView.offset);
  const [error, setError] = useState<string | null>(null);
  /** Sunucu taraflı sıralama/filtre (Tasarım V4, Öneri 8). */
  const [sort, setSort] = useState<LibrarySort>(initialView.sort);
  const [filter, setFilter] = useState<LibraryFilter>(initialView.filter);
  /**
   * Hesap varsayılanı yalnız bu oturumda bu kütüphane için kayıt yokken
   * uygulanır. İlk boyamada boş görünümü sessionStorage'a yazmıyoruz; aksi
   * hâlde varsayılan sıralama hiç yüklenemezdi.
   */
  const [viewReady, setViewReady] = useState(() => hasStoredLibraryView(libraryId));

  useEffect(() => {
    if (hasStoredLibraryView(libraryId)) return;
    let cancelled = false;
    void api
      .catalogPreferences()
      .then((preferences) => {
        if (!cancelled) setSort(preferences.defaultLibrarySort);
      })
      .catch(() => {
        // Tercih okunamazsa A-Z güvenli varsayılanı kalır.
      })
      .finally(() => {
        if (!cancelled) setViewReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [libraryId]);

  useEffect(() => {
    if (viewReady) saveLibraryView(libraryId, { sort, filter, offset });
  }, [viewReady, libraryId, sort, filter, offset]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** Izgara/liste ve kart boyutu tercihi `localStorage`'da saklanır. */
  const view = useViewPrefs();

  const isMusicLib = isMusicLibrary(library);
  const musicPageSize = isMusicLib ? 200 : PAGE_SIZE;
  const fetchOffset = isMusicLib ? 0 : offset;

  /*
   * Müzik albümleri ayrı bir uçtan gelir: başlık/sanatçı kullanıcı
   * düzenlemesini, kapak da albüm kapağını yansıtır. Parçalar `albumId` ile
   * bu kayıtlara bağlanır; etiketsiz klasörler "Bilinmeyen Albüm"de birleşmez.
   */
  useEffect(() => {
    if (!isMusicLib) {
      setMusicAlbums([]);
      return;
    }
    let cancelled = false;
    void api
      .music()
      .then((archive) => {
        if (!cancelled) setMusicAlbums(archive.albums);
      })
      .catch(() => {
        // Albüm künyesi okunamazsa parça etiketleriyle gruplama sürer.
        if (!cancelled) setMusicAlbums([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isMusicLib]);

  const fetchItems = () => {
    void api
      .libraryItems(libraryId, fetchOffset, musicPageSize, sort, filter)
      .then((page) => {
        setItems(page.items);
        setTotal(page.total);
        if (user !== null) {
          void saveOfflineLibraryPage(user.id, {
            libraryId,
            offset: fetchOffset,
            sort,
            filter,
            items: page.items,
            total: page.total,
          });
        }
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Öğeler okunamadı.');
      });
  };

  const fetchSummary = (id: string) => {
    void api
      .librarySummary(id)
      .then((data) => setSummary(data))
      .catch(() => setSummary(null));
  };

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);

    const applyPage = (page: { items: Item[]; total: number }): void => {
      setItems(page.items);
      setTotal(page.total);
      if (user !== null) {
        void saveOfflineLibraryPage(user.id, {
          libraryId,
          offset: fetchOffset,
          sort,
          filter,
          items: page.items,
          total: page.total,
        });
      }
    };

    const showCachedPage = (cached: OfflineLibraryPage): void => {
      setItems(cached.items);
      setTotal(cached.total);
      setError(null);
      if (cached.offset !== offset) setOffset(cached.offset);
    };

    const handleError = async (caught: unknown): Promise<void> => {
      if (cancelled) return;
      if (user !== null) {
        const cached = await loadOfflineLibraryPage(user.id, libraryId, sort, filter, fetchOffset);
        if (!cancelled && cached !== null) {
          showCachedPage(cached);
          return;
        }
      }
      if (!cancelled) {
        setError(caught instanceof Error ? caught.message : 'Öğeler okunamadı.');
      }
    };

    void api
      .libraryItems(libraryId, fetchOffset, musicPageSize, sort, filter)
      .then((page) => {
        if (!cancelled) applyPage(page);
      })
      .catch(handleError);
    return () => {
      cancelled = true;
    };
  }, [libraryId, fetchOffset, musicPageSize, sort, filter, offset, user]);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    void api
      .librarySummary(libraryId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [libraryId]);

  const handleScan = async () => {
    if (!canManage || isScanning) return;
    setIsScanning(true);
    setScanProgress(null);

    try {
      await api.scanLibrary(libraryId, true);

      const interval = setInterval(async () => {
        try {
          const status = await api.scanStatus(libraryId);
          if (status.running) {
            setScanProgress(status.total > 0 ? `${status.done}/${status.total}` : 'aranıyor');
          } else {
            clearInterval(interval);
            setIsScanning(false);
            setScanProgress(null);
            /*
             * Arka plan taraması başarısız olabilir (uzak mount koptu, EIO,
             * izin hatası). Durum ucundaki `error` yok sayılırsa kullanıcı
             * "tarama bitti ama yeni dosya gelmedi" görür ve tekrar tekrar
             * tarar; gerçek nedeni hiç öğrenmez.
             */
            if (status.error) {
              setError(`Tarama tamamlanamadı: ${status.error}`);
              return;
            }
            fetchItems();
            fetchSummary(libraryId);
            onRefreshLibraries?.();
          }
        } catch {
          clearInterval(interval);
          setIsScanning(false);
        }
      }, 1000);
    } catch (err) {
      setIsScanning(false);
      setError(err instanceof Error ? err.message : 'Tarama başlatılamadı.');
    }
  };

  const handleDelete = async () => {
    if (!canManage) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteLibrary(libraryId);
      setShowDeleteModal(false);
      onRefreshLibraries?.();
      onDeleted?.();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Kütüphane silinemedi.');
      setIsDeleting(false);
    }
  };

  /** Sıralama değişince ilk sayfaya dön. */
  const handleSortChange = (next: LibrarySort): void => {
    setOffset(0);
    setSort(next);
  };

  /** Filtre değişince ilk sayfaya dön. */
  const handleFilterChange = (next: LibraryFilter): void => {
    setOffset(0);
    setFilter(next);
  };

  const theme = getLibraryTheme(library?.kind, library?.name);
  const isReachable = library?.reachable ?? true;

  /*
   * "Son Eklenenler" bölümü yalnızca bir **alt küme** gösterdiğinde anlamlı.
   *
   * Kısa kütüphanelerde (ekran görüntüsündeki gibi 2 parça) bölüm, hemen
   * altındaki "Tüm İçerikler" ile **aynı iki kartı** tekrar gösteriyordu:
   * kullanıcı aynı ekranda aynı içeriği iki kez görüyor ve boşluk
   * doldurulmuyor. Tek koşul yeterli: `recent` listenin tamamını kapsıyorsa
   * bölüm hiç render edilmez.
   */
  const showRecent =
    !isMusicLib &&
    filter === 'all' &&
    summary !== null &&
    summary.recent.length > 0 &&
    summary.recent.length < total;

  return (
    <div className="library-screen">
      <LibraryHero
        library={library}
        total={total}
        summary={summary}
        theme={theme}
        isScanning={isScanning}
        scanProgress={scanProgress}
        canManage={canManage}
        onScan={() => void handleScan()}
        onOpenDelete={() => setShowDeleteModal(true)}
        hideScan={items !== null && items.length === 0 && filter === 'all'}
      />

      {canManage && showDeleteModal && (
        <LibraryDeleteModal
          libraryName={library?.name || 'Kütüphane'}
          isDeleting={isDeleting}
          error={deleteError}
          onConfirm={() => void handleDelete()}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {error && <ErrorNotice message={error} onRetry={fetchItems} />}

      {library !== null && !library.reachable && (
        <div className="state state--error" role="alert">
          <p className="state__message">
            <strong>Bu kütüphane şu anda erişilemiyor</strong> ({library.unavailableReason}). Ev
            bilgisayarının açık ve WireGuard tünelinin bağlı olduğundan emin olun. Aşağıdaki
            içerikler listelenir ama oynatılamaz.
          </p>
          {onRefreshLibraries && (
            <button
              type="button"
              className="button button--ghost state__action"
              onClick={onRefreshLibraries}
            >
              Tekrar kontrol et
            </button>
          )}
        </div>
      )}

      {/*
        B-23 · Geniş ekranda **yan ray**.

        Önceden kütüphane ekranı tek sütundu: istatistik kutuları üstte
        yatay bir bant, altında kartlar. 1–2 içerikli bir kütüphanede sayfanın
        %55'i boştu ve "bu kütüphane neden bu kadar az?" sorusu cevapsız
        kalıyordu.

        Artık ≥1280 px'de sağda 320 px'lik bir ray var; ray kütüphanenin
        **özetini** taşır. Dar ekranda ray gizlenir ve özet başlık bandının
        içine (tek satır) sığar.
      */}
      <div className={`library-screen__body ${summary ? 'has-rail' : ''}`}>
        <div className="library-screen__main">
          {items === null && !error && <Loading />}

          {items !== null && items.length === 0 && filter === 'all' && (
            <LibraryEmpty
              theme={theme}
              isScanning={isScanning}
              isReachable={isReachable}
              canScan={canManage}
              onScan={() => void handleScan()}
            />
          )}

          {items !== null && items.length === 0 && filter !== 'all' && (
            <div className="library-empty">
              <h2 className="library-empty__title">Bu filtreye uygun içerik yok</h2>
              <p className="library-empty__desc">
                Kütüphanede içerik var, ancak seçtiğiniz izlenme filtresine uyan bir öğe bulunmuyor.
              </p>
              <button
                type="button"
                className="button button--primary library-empty__btn"
                onClick={() => handleFilterChange('all')}
              >
                Tüm içerikleri göster
              </button>
            </div>
          )}

          {/*
            B-23 · Az içerikli kütüphane notu.

            Boşluğu **bilgi** ile doldurur: kütüphane yeni olduğunda ya da
            taramadan sonra da içerik bulunamadığında kullanıcı nedenini
            öğrenir. Sayfa "kesilmiş" görünmez.
          */}
          {items !== null && items.length > 0 && total <= FEW_ITEMS_LIMIT && (
            <p className="library-screen__hint">
              Bu kütüphane henüz yeni. Klasöre yeni medya eklediğinizde{' '}
              <strong>Kütüphaneyi Tara</strong> düğmesiyle içerikler burada listelenir.
            </p>
          )}

          {offset === 0 && showRecent && (
            <LibraryRecentSection
              recent={summary.recent}
              isMusicLib={isMusicLib}
              reachable={isReachable}
              viewClassName={view.gridClassName}
              onWatch={onWatch}
              onOpenItem={onOpenItem}
              onEnqueue={onEnqueue}
            />
          )}

          {items !== null && items.length > 0 && isMusicLib && (
            <MusicLibraryGroups
              items={items}
              albums={musicAlbums}
              view={view}
              onWatch={onWatch}
              onEnqueue={onEnqueue}
              onOpenAlbum={onOpenAlbum}
            />
          )}

          {items !== null && items.length > 0 && !isMusicLib && (
            <LibraryCatalog
              items={items}
              total={total}
              offset={offset}
              showRecent={showRecent}
              theme={theme}
              isMusicLib={isMusicLib}
              isReachable={isReachable}
              view={view}
              sort={sort}
              filter={filter}
              onSortChange={handleSortChange}
              onFilterChange={handleFilterChange}
              onWatch={onWatch}
              onOpenItem={onOpenItem}
              onEnqueue={onEnqueue}
              onChangePage={setOffset}
            />
          )}
        </div>

        {summary && (
          <aside className="library-screen__rail" aria-label="Kütüphane Özeti">
            <LibraryRail summary={summary} isMusicLib={isMusicLib} />
          </aside>
        )}
      </div>
    </div>
  );
}

/**
 * Katalog bölümü — kart ızgarası, görünüm çubuğu ve sayfalama.
 *
 * Ayrı bileşen olmasının nedeni: `LibraryScreen` zaten 20+ dal içeriyordu ve
 * yeni "yan ray" düzeniyle karmaşıklık sınırını aştı. Bölüm, veri
 * almaz — hepsini **prop** olarak alır; böylece hangi koşulun neyi
 * etkilediği okunabilir kalır.
 */
function LibraryCatalog({
  items,
  total,
  offset,
  showRecent,
  theme,
  isMusicLib,
  isReachable,
  view,
  sort,
  filter,
  onSortChange,
  onFilterChange,
  onWatch,
  onOpenItem,
  onEnqueue,
  onChangePage,
}: {
  items: Item[];
  total: number;
  offset: number;
  showRecent: boolean;
  theme: LibraryTheme;
  isMusicLib: boolean;
  isReachable: boolean;
  view: ViewPrefsApi;
  sort: LibrarySort;
  filter: LibraryFilter;
  onSortChange: (sort: LibrarySort) => void;
  onFilterChange: (filter: LibraryFilter) => void;
  onWatch: (id: string) => void;
  onOpenItem: (id: string) => void;
  onEnqueue?: ((item: Item) => void) | undefined;
  onChangePage: (offset: number) => void;
}): ReactNode {
  return (
    <section className="section library-screen__catalog-section" aria-labelledby="catalog-title">
      <div className="section__header">
        <div className="section__header-title">
          <span className="section__header-icon">{theme.sectionIcon}</span>
          <h2 id="catalog-title" className="section__title">
            {showRecent ? 'Tüm İçerikler' : 'Kütüphane İçeriği'}
          </h2>
        </div>
        <div className="section__header-actions">
          <span className="section__badge">{total} içerik</span>
          <CatalogFilters
            sort={sort}
            filter={filter}
            onSortChange={onSortChange}
            onFilterChange={onFilterChange}
          />
          <ViewToolbar view={view} />
        </div>
      </div>

      <div
        className={`grid ${view.gridClassName} ${usesShowcaseGrid(total) ? 'grid--few' : ''}`}
        style={
          usesShowcaseGrid(total)
            ? ({ '--few-cols': String(showcaseColumns(total)) } as CSSProperties)
            : undefined
        }
      >
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            layout={isMusicLib ? 'square' : 'portrait'}
            mediaKind={isMusicLib ? 'music' : 'video'}
            href={
              item.kind === 'movie' || item.kind === 'episode'
                ? `#/izle/${item.id}`
                : `#/oge/${item.id}`
            }
            onClick={
              item.kind === 'movie' || item.kind === 'episode'
                ? () => onWatch(item.id)
                : () => onOpenItem(item.id)
            }
            action={
              isMusicLib && onEnqueue !== undefined ? (
                <QueueAction item={item} onEnqueue={onEnqueue} />
              ) : undefined
            }
            unreachable={!isReachable}
          />
        ))}
      </div>

      {/*
        Sayfalama yalnız **birden çok sayfa** varken basılır.

        2 içerikli bir kütüphanede ekran görüntüsünde görüldüğü gibi iki
        düğme de pasif hâlde "1–2 / 2" yazıyordu: kullanıcı hiçbir yere
        gidemeyen bir kontrol görüyor. Tek sayfada gereken tek bilgi zaten
        başlıktaki "2 içerik" rozetinde var.
      */}
      {showsPager(total, PAGE_SIZE) && (
        <nav className="pager-dock" aria-label="Sayfalama">
          <button
            type="button"
            className="button button--ghost pager-dock__btn"
            disabled={offset === 0}
            onClick={() => onChangePage(Math.max(0, offset - PAGE_SIZE))}
          >
            <ArrowLeftIcon size={14} />
            Önceki
          </button>
          <span className="pager-dock__info">
            {offset + 1}–{Math.min(offset + items.length, total)} / {total}
          </span>
          <button
            type="button"
            className="button button--ghost pager-dock__btn"
            disabled={offset + items.length >= total}
            onClick={() => onChangePage(offset + PAGE_SIZE)}
          >
            Sonraki
            <ArrowRightIcon size={14} />
          </button>
        </nav>
      )}
    </section>
  );
}

/**
 * Sıralama ve izlenme filtresi (Tasarım V4, Öneri 8).
 *
 * Sıralama/filtre **sunucuda** uygulanır; istemci yalnız seçimi taşır.
 * Böylece 50+ içerikli kütüphanede sayfalama toplam sayıyı doğru bildirir.
 */
function CatalogFilters({
  sort,
  filter,
  onSortChange,
  onFilterChange,
}: {
  sort: LibrarySort;
  filter: LibraryFilter;
  onSortChange: (sort: LibrarySort) => void;
  onFilterChange: (filter: LibraryFilter) => void;
}): ReactNode {
  const filters: Array<{ value: LibraryFilter; label: string }> = [
    { value: 'all', label: 'Tümü' },
    { value: 'unwatched', label: 'İzlenmemiş' },
    { value: 'watched', label: 'İzlenmiş' },
  ];

  return (
    <div className="catalog-filters">
      <label className="catalog-filters__sort">
        <span className="visually-hidden">Sıralama</span>
        <select
          className="catalog-filters__select"
          value={sort}
          onChange={(event) => onSortChange(event.target.value as LibrarySort)}
        >
          <option value="title">A–Z</option>
          <option value="added">Son eklenen</option>
          <option value="year">Yıl</option>
          <option value="duration">Süre</option>
        </select>
      </label>

      <fieldset className="catalog-filters__chips">
        <legend className="visually-hidden">İzlenme filtresi</legend>
        {filters.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`catalog-filters__chip ${filter === item.value ? 'is-active' : ''}`}
            aria-pressed={filter === item.value}
            onClick={() => onFilterChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </fieldset>
    </div>
  );
}

/**
 * Kütüphane özeti (yan ray).
 *
 * ## Bilgi hiyerarşisi (B-24)
 *
 * Önceden altı **eşit ağırlıklı** istatistik kutusu vardı: "Toplam İçerik",
 * "Film", "Dizi", "Toplam Süre", "Son Eklenen" ve — en yanlış yerde —
 * "Son Tarama". Tarama zamanı bir operasyonel log kaydıdır; kullanıcının
 * içerik kararıyla ilgisi yoktur.
 *
 * Yeni hiyerarşi:
 *
 *   1. **Birincil** — içerik sayısı ve toplam süre (kullanıcının sorduğu).
 *   2. **İkincil** — tür dağılımı ve son eklenen tarihi.
 *   3. **Üçüncül** — son tarama zamanı; küçük ve sessiz.
 *
 * "Son Tarama" karar gereği kütüphane ekranından **kaldırıldı**; hesap
 * ekranındaki "Kütüphane Taramaları" listesine taşındı.
 */
function LibraryRail({
  summary,
  isMusicLib,
}: {
  summary: LibrarySummary;
  /** Müzik kütüphanesinde `movies` kovası "Müzik" olarak etiketlenir. */
  isMusicLib: boolean;
}): ReactNode {
  const { stats } = summary;

  return (
    <div className="library-rail">
      <h2 className="library-rail__title">Özet</h2>

      <div className="library-rail__primary">
        <span className="library-rail__primary-value">{stats.total}</span>
        <span className="library-rail__primary-label">içerik</span>
      </div>

      {stats.durationMs > 0 && (
        <p className="library-rail__primary-sub">{formatTotalDuration(stats.durationMs)}</p>
      )}

      <dl className="library-rail__rows">
        {stats.movies > 0 && (
          <div className="library-rail__row">
            <dt>{isMusicLib ? 'Müzik' : 'Film'}</dt>
            <dd>{stats.movies}</dd>
          </div>
        )}
        {stats.shows > 0 && (
          <div className="library-rail__row">
            <dt>Dizi</dt>
            <dd>{stats.shows}</dd>
          </div>
        )}
        {stats.newestAt !== null && (
          <div className="library-rail__row">
            <dt>Son eklenen</dt>
            <dd>{formatDate(stats.newestAt)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function LibraryRecentSection({
  recent,
  isMusicLib,
  reachable,
  viewClassName,
  onWatch,
  onOpenItem,
  onEnqueue,
}: {
  recent: Item[];
  isMusicLib: boolean;
  reachable: boolean;
  viewClassName: string;
  onWatch: (id: string) => void;
  onOpenItem: (id: string) => void;
  onEnqueue?: ((item: Item) => void) | undefined;
}): ReactNode {
  return (
    <section className="section library-screen__recent-section" aria-labelledby="recent-title">
      <div className="section__header">
        <div className="section__header-title">
          <span className="section__header-icon">
            <SparklesIcon {...SECTION_ICON} />
          </span>
          <h2 id="recent-title" className="section__title">
            Son Eklenenler
          </h2>
        </div>
        <span className="section__badge">{recent.length} içerik</span>
      </div>

      <div className={`grid ${viewClassName}`}>
        {recent.map((item) => (
          <ItemCard
            key={`recent-${item.id}`}
            item={item}
            layout={isMusicLib ? 'square' : 'portrait'}
            mediaKind={isMusicLib ? 'music' : 'video'}
            href={
              item.kind === 'movie' || item.kind === 'episode'
                ? `#/izle/${item.id}`
                : `#/oge/${item.id}`
            }
            onClick={
              item.kind === 'movie' || item.kind === 'episode'
                ? () => onWatch(item.id)
                : () => onOpenItem(item.id)
            }
            action={
              isMusicLib && onEnqueue !== undefined ? (
                <QueueAction item={item} onEnqueue={onEnqueue} />
              ) : undefined
            }
            unreachable={!reachable}
          />
        ))}
      </div>
    </section>
  );
}

/** Kütüphane kartı kapağı: önce sunucunun imzalı URL'si. */
function previewThumbnail(library: Library | null | undefined): string | null {
  if (!library) return null;
  if (library.previewThumbnailUrl) return library.previewThumbnailUrl;
  return library.previewItemId ? mediaUrl(`/api/items/${library.previewItemId}/thumbnail`) : null;
}

function LibraryHero({
  library,
  total,
  summary,
  theme,
  isScanning,
  scanProgress,
  canManage,
  onScan,
  onOpenDelete,
  hideScan,
}: {
  library: Library | null;
  total: number;
  summary: LibrarySummary | null;
  theme: LibraryTheme;
  isScanning: boolean;
  scanProgress: string | null;
  canManage: boolean;
  onScan: () => void;
  onOpenDelete: () => void;
  /**
   * Boş kütüphanede "Tara" eyleminin sahibi **boş durum kartıdır**.
   * Aynı eylemi kahraman bandında ikinci kez göstermek (biri hayalet,
   * biri dolu) kullanıcıya iki farklı iş varmış gibi görünüyordu.
   */
  hideScan: boolean;
}): ReactNode {
  const previewLocal = previewThumbnail(library);

  return (
    <div className={`library-screen__hero ${theme.themeClass}`}>
      {previewLocal !== null && (
        <div className="library-screen__hero-backdrop" aria-hidden="true">
          <img
            src={resizeProxyImage(library?.previewPosterUrl, 'w780') ?? previewLocal}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(event) => {
              const image = event.currentTarget;
              if (library?.previewPosterUrl && !image.src.endsWith(previewLocal)) {
                image.src = previewLocal;
                return;
              }
              const wrapper = image.parentElement;
              if (wrapper) wrapper.style.display = 'none';
            }}
          />
        </div>
      )}

      <div className="library-screen__hero-info">
        <div className="library-screen__icon-tile" aria-hidden="true">
          <span className="library-screen__icon">{theme.icon}</span>
        </div>

        <div className="library-screen__details">
          <div className="library-screen__chips">
            <span className="library-screen__chip-kind">{theme.typeLabel}</span>
            {library?.layer === 'cold' && (
              <span className="library-screen__chip library-screen__chip--cold">
                <LockIcon {...SECTION_ICON} />
                Ev Tüneli
              </span>
            )}
            <span className="library-screen__chip">{total} içerik</span>
          </div>

          <h1 className="library-screen__title">{library?.name || 'Kütüphane'}</h1>

          {/*
            B-24 · Tek satırlık özet.

            Yan ray (≥1280 px) yokken bu satır kütüphanenin özetini taşır:
            "12 içerik · 8 saat 40 dakika · son eklenen 18 Eki". Önceden
            aynı bilgi altı ayrı kutuda, eşit ağırlıkta duruyordu.
          */}
          {summary && (
            <p className="library-screen__summary-line">
              <strong>{summary.stats.total}</strong> içerik
              {summary.stats.durationMs > 0 && (
                <>
                  {' · '}
                  {formatTotalDuration(summary.stats.durationMs)}
                </>
              )}
              {summary.stats.newestAt !== null && (
                <>
                  {' · son eklenen '}
                  {formatDate(summary.stats.newestAt)}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="library-screen__hero-actions">
        <span className="visually-hidden" role="status" aria-live="polite">
          {isScanning
            ? scanProgress
              ? `Kütüphane taranıyor: ${scanProgress}`
              : 'Kütüphane taranıyor'
            : ''}
        </span>
        {canManage && !hideScan && (
          <button
            type="button"
            className={`button button--ghost library-screen__scan-btn ${isScanning ? 'is-scanning' : ''}`}
            onClick={onScan}
            disabled={isScanning || (library !== null && !library.reachable)}
          >
            {isScanning ? (
              <>
                <span className="spinner-icon">
                  <RefreshIcon size={16} />
                </span>
                <span>{scanProgress ? `Taranıyor (${scanProgress})` : 'Taranıyor…'}</span>
              </>
            ) : (
              <>
                <RefreshIcon size={16} />
                <span>Kütüphaneyi Tara</span>
              </>
            )}
          </button>
        )}

        {/*
          Yıkıcı eylem **taşma menüsünde**.

          Önceden "Kütüphaneyi Sil" kırmızı çerçeveli bir düğme olarak
          "Kütüphaneyi Tara"nın hemen yanında duruyordu: aynı boyut, aynı
          hiza, aynı görsel ağırlık. Rutin bir eylemle geri alınamaz bir
          eylemi yan yana koymak yanlış tıklama maliyetini yükseltiyordu.
          Artık birincil alan yalnız "Tara"ya ait; silme ikincil menüde.
        */}
        {canManage && <LibraryActionsMenu onDelete={onOpenDelete} />}
      </div>
    </div>
  );
}

/**
 * Kütüphane işlemleri menüsü — yıkıcı eylem burada yaşar.
 *
 * `aria-haspopup="menu"` + `aria-expanded` + `role="menu"`/`menuitem`
 * ile klavye ve ekran okuyucu için standart bir taşma menüsü. Escape
 * tuşu menüyü kapatır; arka plan katmanı dışarı tıklamayı yakalar.
 */
function LibraryActionsMenu({ onDelete }: { onDelete: () => void }): ReactNode {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div className="library-menu">
      <button
        type="button"
        className="button button--ghost library-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Kütüphane işlemleri"
        title="Kütüphane işlemleri"
        onClick={() => setOpen((value) => !value)}
      >
        <MoreIcon size={18} />
      </button>

      {open && (
        <>
          <button
            type="button"
            className="library-menu__backdrop"
            aria-label="Menüyü kapat"
            tabIndex={-1}
            onClick={() => setOpen(false)}
          />
          <div className="library-menu__panel" role="menu">
            <button
              type="button"
              role="menuitem"
              className="library-menu__item library-menu__item--danger"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              <TrashIcon size={16} />
              <span>Kütüphaneyi Sil</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function LibraryEmpty({
  theme,
  isScanning,
  isReachable,
  canScan,
  onScan,
}: {
  theme: LibraryTheme;
  isScanning: boolean;
  isReachable: boolean;
  canScan: boolean;
  onScan: () => void;
}): ReactNode {
  return (
    <div className="library-empty">
      <div className="library-empty__icon-frame" aria-hidden="true">
        <span className="library-empty__icon">{theme.emptyIcon}</span>
      </div>
      <h2 className="library-empty__title">Bu Kütüphanede Henüz İçerik Yok</h2>
      <p className="library-empty__desc">
        {canScan
          ? 'Sunucudaki klasörünüze medya dosyaları ekledikten sonra aşağıdaki butona tıklayarak kütüphaneyi tarayabilir ve içerikleri buraya aktarabilirsiniz.'
          : 'Bu klasörde henüz medya yok. İçerik eklemek ve taramak için bir yöneticiye başvurun.'}
      </p>
      {canScan && (
        <button
          type="button"
          className="button button--primary library-empty__btn"
          onClick={onScan}
          disabled={isScanning || !isReachable}
        >
          {isScanning ? (
            <>
              <span className="spinner-icon">
                <RefreshIcon size={16} />
              </span>
              <span>Taranıyor…</span>
            </>
          ) : (
            <>
              <RefreshIcon size={16} />
              <span>Kütüphaneyi Şimdi Tara</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Kütüphane silme onay penceresi.
 *
 * Not: Karta "Oynat / Bölümler" düğmesi eklemek yerine kartın kendisi
 * tıklanabilir yapıldı — düğmenin yaptığı iş birebir aynıydı ve
 * `button--primary` ile birleşince tasarımı eziliyordu. Bkz. `ItemCard`.
 */
/** Müzik kartındaki "Sıraya ekle" eylemi; oynatmayı kesmez. */
function QueueAction({
  item,
  onEnqueue,
}: {
  item: Item;
  onEnqueue: (item: Item) => void;
}): ReactNode {
  return (
    <button
      type="button"
      className="card__action card__queue-action"
      onClick={() => onEnqueue(item)}
      aria-label={`${item.title} parçasını sıraya ekle`}
      title="Sıraya ekle"
    >
      <QueueIcon size={15} />
      Sıraya ekle
    </button>
  );
}

function LibraryDeleteModal({
  libraryName,
  isDeleting,
  error,
  onConfirm,
  onCancel,
}: {
  libraryName: string;
  isDeleting: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, () => {
    if (!isDeleting) onCancel();
  });

  return (
    <div className="modal-backdrop">
      <button
        type="button"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          width: '100%',
          height: '100%',
        }}
        onClick={() => {
          if (!isDeleting) onCancel();
        }}
        aria-label="Kapat"
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        className="card modal"
        role="dialog"
        aria-labelledby="delete-library-title"
        aria-modal="true"
        tabIndex={-1}
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '460px',
          padding: '28px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
        }}
      >
        <h2
          id="delete-library-title"
          className="card__title"
          style={{
            color: 'var(--danger)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '0 0 12px',
            fontSize: '1.25rem',
          }}
        >
          <AlertIcon size={20} />
          <span>Kütüphaneyi Sil</span>
        </h2>

        <p
          style={{
            margin: '0 0 10px',
            fontSize: '0.98rem',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}
        >
          <strong>"{libraryName}"</strong> kütüphanesini ve veritabanındaki tüm katalog kayıtlarını
          silmek istediğinize emin misiniz?
        </p>

        <p
          style={{
            margin: '0 0 20px',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
            lineHeight: 1.4,
          }}
        >
          <InfoIcon size={15} />
          <strong>Not:</strong> Sunucudaki disk dosyalarınız fiziksel olarak silinmez, yalnızca
          sistemden ve oynatma listelerinden kaldırılır.
        </p>

        {error && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorNotice message={error} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            type="button"
            className="button button--ghost"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Vazgeç
          </button>
          <button
            type="button"
            className="button"
            style={{
              background: 'var(--danger)',
              color: '#ffffff',
              border: 'none',
              padding: '10px 18px',
              fontWeight: 600,
            }}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Siliniyor…' : 'Evet, Kütüphaneyi Sil'}
          </button>
        </div>
      </div>
    </div>
  );
}
